import { authApi } from '~/api/auth'
import { BaseMessage } from '~/utils/baseMessage'

/**
 * @description 认证组合函数，用于管理用户认证状态
 * @returns {Object} 认证方法和状态
 */
export const useAuth = () => {
  let userStore
  try {
    userStore = useUserStore()
  } catch (err) {
    userStore = {
      userInfo: null,
      isLoggedIn: false,
      setUserInfo: () => {},
      logout: () => {},
    }
  }

  const isLoading = ref(false)
  const error = ref(null)

  /**
   * @description 使用邮箱和密码登录
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @returns {Promise<Object>} 登录响应
   */
  const login = async (email, password) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.login(email, password)
      userStore.setUserInfo(response.user)
      return response
    } catch (err) {
      error.value = err.response?.data?.message || '登录失败'
      BaseMessage.error(error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * @description 注册新用户
   * @param {Object} userData - 用户注册数据
   * @returns {Promise<Object>} 注册响应
   */
  const register = async (userData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.register(userData)
      userStore.setUserInfo(response.user)
      return response
    } catch (err) {
      error.value = err.response?.data?.message || '注册失败'
      BaseMessage.error(error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * @description 登出当前用户
   * @description 调用成都退出接口（userStore.logout 内部会调用），然后清除本地状态
   */
  const logout = async () => {
    isLoading.value = true

    try {
      // 调用退出接口，然后清除本地状态（userStore.logout 内部会调用成都退出接口）
      await userStore.logout()
      isLoading.value = false
      await navigateTo('/')
    } catch (err) {
      // 即使退出接口失败，也清除本地状态
      console.error('退出登录失败:', err)
      await userStore.logout(true) // 跳过 API 调用，直接清除本地状态
      isLoading.value = false
      await navigateTo('/')
    }
  }

  /**
   * @description 获取当前登录用户信息
   * @returns {Promise<Object>} 用户信息
   */
  const fetchUserInfo = async () => {
    isLoading.value = true
    error.value = null

    try {
      return await userStore.fetchUserInfo()
    } catch (err) {
      error.value = err.response?.data?.message || '获取用户信息失败'
      // 不在这里 BaseMessage.error，让调用者决定是否提示
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * @description 检查用户是否拥有指定角色
   * @param {string} role - 要检查的角色
   * @returns {boolean} 用户是否拥有该角色
   */
  const hasRole = (role) => {
    if (!userStore.userInfo) return false
    return userStore.userInfo.roles?.includes(role) || false
  }

  /**
   * @description 检查用户是否拥有指定角色中的任意一个
   * @param {Array<string>} roles - 要检查的角色列表
   * @returns {boolean} 用户是否拥有任意一个角色
   */
  const hasAnyRole = (roles) => {
    if (!userStore.userInfo || !roles?.length) return false
    return roles.some((role) => userStore.userInfo.roles?.includes(role))
  }

  /**
   * @description 检查用户是否拥有所有指定的角色
   * @param {Array<string>} roles - 要检查的角色列表
   * @returns {boolean} 用户是否拥有所有角色
   */
  const hasAllRoles = (roles) => {
    if (!userStore.userInfo || !roles?.length) return false
    return roles.every((role) => userStore.userInfo.roles?.includes(role))
  }

  return {
    isLoading,
    error,
    login,
    register,
    logout,
    fetchUserInfo,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  }
}
